import { ReactNode, useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronUp } from '@carbon/react/icons';
import styles from '../expand.module.scss';

// A collapsible section for the expanded card (Parties, Transaction Table, Title
// Workspace). A full-width header row with the section label and a chevron; the
// body is mounted only while open, so lazy-loading children fetch on first open.
export const ExpandAccordion = ({
  title,
  defaultOpen = false,
  openSignal,
  children,
}: {
  title: ReactNode;
  defaultOpen?: boolean;
  // Broadcast from the card's Expand-All / Collapse-All control. A fresh object
  // on each click re-syncs this section's open state; manual toggling still
  // works afterward (the effect only fires when the object reference changes).
  openSignal?: { open: boolean };
  children: ReactNode;
}) => {
  // An openSignal already set to open at mount means this section is mounting
  // inside a card that just expanded via Expand-All or prev/next navigation — the
  // body mounts a render after the signal is set, so the signal is "already
  // present". Honor an open:true at mount so those sections actually open; a
  // stale open:false at mount is still ignored (below), so a defaultOpen section
  // isn't force-closed by a lingering Collapse-All.
  const initialOpen = defaultOpen || openSignal?.open === true;
  const [open, setOpen] = useState(initialOpen);
  // Mount the body once, on first open, then keep it mounted: children lazy-load
  // on mount, so we don't want it while collapsed, but keeping it after lets the
  // height reveal animate the collapse too.
  const everOpenRef = useRef(false);
  everOpenRef.current ||= open;
  // Only react to signal CHANGES, not to a persisted signal present at mount:
  // otherwise a section opened by defaultOpen is force-closed the first render
  // when a stale Collapse-All signal is still set.
  const didMountSignal = useRef(false);
  useEffect(() => {
    if (!didMountSignal.current) {
      didMountSignal.current = true;
      return;
    }
    if (openSignal) setOpen(openSignal.open);
  }, [openSignal]);
  return (
    <section className={styles.accordion}>
      <button
        type="button"
        className={styles.accordionHeader}
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((value) => !value);
        }}
      >
        <span className={styles.accordionTitle}>{title}</span>
        {open ? <ChevronUp /> : <ChevronDown />}
      </button>
      <div
        className={[
          styles.accordionReveal,
          open ? styles.accordionRevealOpen : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {everOpenRef.current && (
          <div className={styles.accordionBody}>
            <div className={styles.accordionBodyInner}>{children}</div>
          </div>
        )}
      </div>
    </section>
  );
};
